"use client"

import {type ReactNode, useState, useCallback, useEffect} from "react"
import {usePathname} from "next/navigation"
import {PanelRightExpandFilled} from "@/common/components/fluent-icons"
import {ResizablePanelGroup, ResizablePanel, ResizableHandle} from "@/common/components/ui/resizable"
import {Sheet, SheetContent} from "@/common/components/ui/sheet"
import {Button} from "@/common/components/ui/button"
import {useIsMobile} from "@/common/hooks/use-mobile"
import {DocsSidebarProvider, useDocsSidebar} from "./docs-sidebar-context"
import {DocsSectionNavMobile} from "./docs-section-nav"

const STORAGE_KEY = "docs-left-sidebar-layout"

interface DocsResizableLayoutProps {
    leftSidebar: ReactNode
    children: ReactNode
}

export function DocsResizableLayout({leftSidebar, children}: DocsResizableLayoutProps) {
    return (
        <DocsSidebarProvider>
            <DocsResizableLayoutInner leftSidebar={leftSidebar}>
                {children}
            </DocsResizableLayoutInner>
        </DocsSidebarProvider>
    )
}

function DocsResizableLayoutInner({leftSidebar, children}: DocsResizableLayoutProps) {
    const isMobile = useIsMobile()
    const {rightSidebarContent} = useDocsSidebar()
    const [leftSheetOpen, setLeftSheetOpen] = useState(false)
    const [savedLayout, setSavedLayout] = useState<Record<string, number> | undefined>(undefined)
    const pathname = usePathname()

    // Navigating via a drawer link keeps the layout (and the open sheet) mounted;
    // close it so the new page is visible.
    useEffect(() => {
        setLeftSheetOpen(false)
    }, [pathname])

    // Flag the open drawer on <body> so the site hamburger (a separate fixed
    // button above the sheet's z-index) can hide itself while the drawer is up.
    useEffect(() => {
        document.body.toggleAttribute("data-docs-drawer-open", leftSheetOpen && isMobile)
        return () => {
            document.body.removeAttribute("data-docs-drawer-open")
        }
    }, [leftSheetOpen, isMobile])

    useEffect(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY)
            if (saved) setSavedLayout(JSON.parse(saved))
        } catch {}
    }, [])

    const handleLayoutChanged = useCallback((layout: Record<string, number>) => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(layout))
        } catch {}
    }, [])

    // Below md (768px): hamburger nav. The docs shell is h-screen overflow-hidden,
    // so this branch must be its own scroll container (the desktop branch scrolls
    // inside its panels) or the page cannot scroll at all.
    if (isMobile) {
        return (
            <div className="flex flex-1 min-h-0 w-full mx-auto border-t relative overflow-y-auto">
                <Sheet open={leftSheetOpen} onOpenChange={setLeftSheetOpen}>
                    <SheetContent side="right" title="Navigation" className="w-72 p-0 pt-12 overflow-y-auto scrollbar-none">
                        <div className="px-5 pb-8">
                            {/* Section switcher: the header tabs are md+ only, so the
                                drawer is the only place to change sections on mobile. */}
                            <div className="mb-6 pb-6 border-b border-border -mx-5 px-5">
                                <DocsSectionNavMobile/>
                            </div>
                            {leftSidebar}
                            {rightSidebarContent && (
                                <div className="mt-6 pt-6 border-t border-border -mx-5 px-5">
                                    {rightSidebarContent}
                                </div>
                            )}
                        </div>
                    </SheetContent>
                </Sheet>
                {/* Docs-tree drawer trigger: right side, tucked under the site hamburger */}
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setLeftSheetOpen(true)}
                    className="fixed right-2.5 top-14 z-30 h-8 w-8 rounded-md border bg-background/80 backdrop-blur-sm shadow-sm"
                    title="Open navigation"
                >
                    <PanelRightExpandFilled className="size-4"/>
                </Button>
                {/* Full width so band backgrounds (header/action bar) bleed edge to
                    edge; the content inside caps itself at 90% on mobile. */}
                <main className="flex-1 flex flex-col min-w-0 relative z-10 w-full">
                    {children}
                </main>
            </div>
        )
    }

    return (
        <div className="flex flex-1 w-full mx-auto border-t h-[calc(100vh-4rem)] overflow-hidden">
            <ResizablePanelGroup
                orientation="horizontal"
                defaultLayout={savedLayout}
                onLayoutChanged={handleLayoutChanged}
                className="h-full"
            >
                <ResizablePanel
                    id="left-sidebar"
                    defaultSize={260}
                    minSize={200}
                    maxSize={320}
                >
                    <aside className="h-full overflow-y-auto scrollbar-none bg-fern px-5 pt-5">
                        {leftSidebar}
                        {/* Show right sidebar content here when < xl */}
                        {rightSidebarContent && (
                            <div className="mt-6 pt-6 border-t border-border -mx-5 px-5">
                                {rightSidebarContent}
                            </div>
                        )}
                    </aside>
                </ResizablePanel>

                <ResizableHandle/>

                <ResizablePanel id="main-content" minSize="50%">
                    <main className="min-w-0 relative z-10 h-full overflow-y-auto">
                        {children}
                    </main>
                </ResizablePanel>
            </ResizablePanelGroup>
        </div>
    )
}
